import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubscription, getBillingHistory, cancelSubscription } from '../lib/api';
import { getUser } from '../lib/auth';
import { toast } from 'sonner';
import MobileNav from '../components/MobileNav';
import { ArrowLeft, CreditCard, Calendar, Receipt, AlertTriangle, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function BillingPage() {
  const [subscription, setSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const navigate = useNavigate();
  const user = getUser();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subRes, historyRes] = await Promise.all([
        getSubscription(),
        getBillingHistory()
      ]);
      setSubscription(subRes.data);
      setBillingHistory(historyRes.data.invoices || []);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      await cancelSubscription();
      toast.success('Subscription cancelled. You will retain access until the end of your billing period.');
      setShowCancelModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatCurrency = (amount, currency = 'gbp') => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-emerald-100 text-emerald-700',
      open: 'bg-blue-100 text-blue-700',
      void: 'bg-slate-100 text-slate-700',
      uncollectible: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      <MobileNav />

      <main className="flex-1 mobile-container md:!max-w-2xl pb-28 md:pb-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <button 
            onClick={() => navigate('/settings')} 
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium mb-6"
          >
            <ArrowLeft size={20} />
            Back to Settings
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold gradient-text pb-1">Billing & Payments</h1>
          <p className="text-slate-600 mt-2">Manage your subscription and view payment history</p>
        </motion.div>

        {/* Current Plan Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-3d p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center">
              <CreditCard size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Current Plan</h2>
              <p className="text-sm text-slate-500">Your subscription details</p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Plan</p>
              <p className="text-lg font-bold text-slate-900 capitalize">{subscription?.current_plan || 'None'}</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Status</p>
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium capitalize ${
                subscription?.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                subscription?.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-700' :
                subscription?.subscription_status === 'canceled' ? 'bg-red-100 text-red-700' :
                'bg-slate-100 text-slate-700'
              }`}>
                {subscription?.subscription_status || 'None'}
              </span>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-sm text-slate-500 mb-1">Next Payment</p>
              <p className="text-lg font-bold text-slate-900">
                {subscription?.current_period_end ? formatDate(subscription.current_period_end) : '-'}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/pricing')}
              className="btn-3d btn-3d-primary h-12 px-6 rounded-xl font-semibold"
            >
              Change Plan
            </button>
            
            {subscription?.subscription_status === 'active' && isAdmin && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="btn-3d h-12 px-6 rounded-xl font-semibold text-red-600 border-2 border-red-200 hover:bg-red-50"
              >
                Cancel Subscription
              </button>
            )}
          </div>
        </motion.div>

        {/* Payment History */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-3d p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center">
              <Receipt size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Payment History</h2>
              <p className="text-sm text-slate-500">Your recent invoices and payments</p>
            </div>
          </div>

          {billingHistory.length === 0 ? (
            <div className="text-center py-12">
              <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No payment history yet</p>
              <p className="text-sm text-slate-400 mt-1">Your invoices will appear here after your first payment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {billingHistory.map((invoice, index) => (
                <motion.div
                  key={invoice.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                      <Receipt size={20} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{invoice.description || 'Subscription'}</p>
                      <p className="text-sm text-slate-500">{formatDate(invoice.created)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(invoice.status)}`}>
                      {invoice.status}
                    </span>
                    <span className="font-bold text-slate-900">{formatCurrency(invoice.amount_paid, invoice.currency)}</span>
                    {invoice.invoice_pdf && (
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        Download
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recurring Payment Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Calendar size={16} className="text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-blue-800">Automatic Recurring Payments</p>
              <p className="text-sm text-blue-600 mt-1">
                Your subscription renews automatically each month. You can cancel anytime and continue using the service until the end of your billing period.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Spacer so content clears the fixed mobile bottom nav */}
        <div className="h-28 md:hidden" aria-hidden="true" />
      </main>

      {/* Cancel Subscription Modal */}
      <AnimatePresence>
        {showCancelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCancelModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle size={32} className="text-red-600" />
              </div>
              
              <h3 className="text-xl font-bold text-slate-900 text-center mb-2">Cancel Subscription?</h3>
              
              <p className="text-slate-600 text-center mb-6">
                Are you sure you want to cancel? You'll keep access until the end of your current billing period, then your account will be downgraded.
              </p>

              <div className="space-y-3">
                <button
                  onClick={handleCancelSubscription}
                  disabled={canceling}
                  className="btn-3d w-full h-12 rounded-xl font-semibold bg-red-600 text-white hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  {canceling ? (
                    <>
                      <div className="spinner-3d w-5 h-5 border-2"></div>
                      Canceling...
                    </>
                  ) : (
                    <>
                      <X size={18} />
                      Yes, Cancel Subscription
                    </>
                  )}
                </button>
                
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="btn-3d btn-3d-outline w-full h-12 rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Check size={18} />
                  Keep My Subscription
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
